// src/components/PlayersList.tsx

import React from 'react';
import styled from 'styled-components';
import { useGame } from '../contexts/GameContext';
import { TimepassSDK } from "../assets/images/TimepassAPI/TimepassSDK";
import { getSequentialColor } from "./CalledNumbers";

const AvatarWrapper = styled.div`
    position: relative;
    width: 60px;
    height: 60px;
`;

const Chip = styled.div`
    position: absolute;
    top: 0px;
    right: -20px;
    background-color: #FFD602;
    color: #000;
    font-size: .70rem;
    font-weight: bold;
    padding: 2px 5px;
    border-radius: 12px;
    white-space: nowrap;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    z-index: 2;
`;

const DashedBox = styled.div<{ $bgColor: string }>`
    width: 70px;
    position: relative;
    margin-top: 10px;
    @media (min-width: 768px) {
        width: 100px;
    }
    background-color: ${({ $bgColor }) => $bgColor};
    border: 2px dashed white;
    border-radius: 10px;
`;

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
`;

const PlayerGrid = styled.div`
    display: flex;
    flex-direction: row;
    white-space: nowrap;
    gap: 12px;
    @media (min-width: 500px) {
        gap: 20px;
    }
`;

const PlayerCard = styled.div<{ $isCurrent: boolean }>`
    min-width: 70px;
    display: flex;
    flex-direction: column;
    align-items: center;
    @media (min-width: 768px) {
        width: 100px;
    }
`;

const Avatar = styled.img<{ $isCurrent: boolean; $you: boolean; $borderColor: string }>`
    width: 60px;
    height: 60px;
    max-width: 100%;
    border-radius: 50%;
    object-fit: cover;
    border: 4px solid ${({ $you, $borderColor }) => $borderColor};
    box-shadow: ${({ $isCurrent }) => ($isCurrent ? '0 0 8px rgba(255,215,0,0.8)' : '0 0 8px rgba(255,215,0,0.8)')};
`;

const dashedBoxColors = ['#03C700', '#ff8e9b', '#FF8b38', '#00C8B7'];

const NameLabel = styled.div`
    color: #000;
    font-weight: 500;
    padding: 6px 6px;
    border-radius: 6px;
    text-align: center;
    font-size: 0.9rem;
    background: none;
    @media (min-width: 768px) {
        width: 100px;
    }
`;

const Image = styled.div`
    width: 35px;
    height: 35px;
    position: absolute;
    left: -22px;
    display: none;
    @media (min-width: 768px) {
        display: block;
        width: 40px;
        height: 40px;
        left: -15px;
    }
`;

const Image2 = styled.div`
    width: 35px;
    height: 35px;
    position: absolute;
    left: -15px;
    top: 50%;
    display: block;
    @media (min-width: 768px) {
        display: none;
    }
`;

const RankOverlay = styled.div`
    position: absolute;
    top: 33%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 0.6rem;
    @media (min-width: 768px) {
        font-size: 0.8rem;
    }
    color: black;
    font-weight: bold;
    pointer-events: none;
`;

const Chip2 = styled.div`
    position: absolute;
    top: 0px;
    transform: translateY(-50%);
    right: -10px;
    padding: 4px 6px;
    border-radius: 12px;
    background-color: #FFD700;
    color: #000;
    font-size: 0.65rem;
    font-weight: bold;
    white-space: nowrap;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    z-index: 2;
`;

const PlayersList: React.FC = () => {
    const { room, players = [], prizeWinners = [], numbersLeft = [] } = useGame();
    const urlParams = new URLSearchParams(window.location.search);
    const playerId = urlParams.get('playerId');
    const timepassSDK = TimepassSDK.getInstance();


    const pointsMap: Record<string, number> = {};
    prizeWinners.forEach(({ prize, player }) => {
        if (player?.playerId) {
            pointsMap[player.playerId] = (pointsMap[player.playerId] || 0) + prize.points;
        }
    });

    const rankedPlayers = [...players]
        .map(p => ({
            ...p,
            points: pointsMap[p.id] || 0,
        }))
        .sort((a, b) => b.points - a.points);

    const rankMap: Record<string, number> = {};
    rankedPlayers.forEach((player, idx) => {
        rankMap[player.id] = idx + 1;
    });

    const numbersLeftMap = React.useMemo(() => {
        const map: Record<string, number> = {};
        numbersLeft.forEach(entry => {
            map[entry.playerId] = entry.numbersLeft;
        });
        return map;
    }, [numbersLeft]);

    const me = players.find((player) => player.id === playerId);
    if (!me || !me.id) return null;


    return (
        <Container>
            <PlayerGrid>
                <PlayerCard key={`${me.id}-me`} $isCurrent={room?.sessionId === me?.sessionId}>
                    <AvatarWrapper>
                        <Image2>
                            <img src={"./rank.png"} style={{ height: '100%', width: '100%' }} />
                            <RankOverlay>{me?.id ? rankMap?.[me.id] ?? '-' : '-'}</RankOverlay>
                        </Image2>
                        <Avatar
                            onClick={() => timepassSDK.showUserProfile(me.id)}
                            $you={true}
                            src={me.profile || '/default-avatar.png'}
                            alt={me.name || 'You'}
                            $isCurrent={room?.sessionId === me?.sessionId}
                            $borderColor={dashedBoxColors[0]}
                        />
                        <Chip>
                            {numbersLeftMap[me.id] ?? 15} left
                        </Chip>
                    </AvatarWrapper>

                    <DashedBox $bgColor={dashedBoxColors[0]}>
                        <Image>
                            <img src={"./rank.png"} style={{ height: '100%', width: '100%' }} />
                            <RankOverlay>{rankMap?.[me.id] ?? '-'}</RankOverlay>
                        </Image>
                        <NameLabel>You</NameLabel>
                        <Chip2>+ {pointsMap?.[me.id] ?? 0}</Chip2>
                    </DashedBox>
                </PlayerCard>

                {players.filter(player => player.id !== playerId).map((player, idx) => {
                    const isCurrent = room?.sessionId === player?.sessionId;
                    const borderColor = getSequentialColor(idx);
                    const colorIndex = (idx + 1) % dashedBoxColors.length;

                    return (
                        <PlayerCard key={`${player.id}-${idx}`} $isCurrent={isCurrent}>
                            <AvatarWrapper>
                                <Image2>
                                    <img src={"./rank.png"} style={{ height: '100%', width: '100%' }} />
                                    <RankOverlay>{rankMap?.[player.id] ?? '-'}</RankOverlay>
                                </Image2>

                                <Avatar
                                    onClick={() => timepassSDK.showUserProfile(player.id)}
                                    $you={false}
                                    src={player.profile || '/default-avatar.png'}
                                    alt={player.name || 'Player'}
                                    $isCurrent={isCurrent}
                                    $borderColor={dashedBoxColors[colorIndex]}
                                />

                                <Chip>{numbersLeftMap[player.id] ?? 15} left</Chip>
                            </AvatarWrapper>

                            <DashedBox $bgColor={dashedBoxColors[colorIndex]}>
                                <Image>
                                    <img src={"./rank.png"} style={{ height: '100%', width: '100%' }} />
                                    <RankOverlay>{rankMap?.[player.id] ?? '-'}</RankOverlay>
                                </Image>
                                <Chip2>+ {pointsMap?.[player.id] ?? 0}</Chip2>
                                <NameLabel>
                                    {player.name?.length > 7 ? player.name.slice(0, 6) + '..' : player.name || 'Player'}
                                </NameLabel>
                            </DashedBox>
                        </PlayerCard>
                    );
                })}
            </PlayerGrid>
        </Container>
    );
};

export default PlayersList;
